<template>
  <v-form ref="form">
    <v-row>
      <v-col cols="12" md="4">
        <v-text-field
          v-model="model.name"
          variant="outlined"
          label="Nome"
          :rules="[required]"
          :readonly="readonly"
          :disabled="loader.loading"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.zip_code"
          v-maska="'#####-###'"
          variant="outlined"
          label="CEP"
          :rules="[required, validateCEP]"
          :readonly="readonly"
          :disabled="loader.loading"
          @update:model-value="debounce(fetchCepData, { cooldown: 1000, immediate: false })"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.city"
          variant="outlined"
          label="Cidade"
          :rules="[required]"
          :readonly="readonly"
          :disabled="!!latestValidCEP"
        />
      </v-col>
      <v-col cols="12" md="2">
        <v-text-field
          v-model="model.state"
          variant="outlined"
          label="Estado"
          :rules="[required, v => v.length == 2 || 'O Estado (UF) deve possuir exatamente 2 caracteres.']"
          :readonly="readonly"
          :disabled="!!latestValidCEP"
          @update:model-value="v => model.state = v.toUpperCase().slice(0, 2)"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.neighborhood"
          variant="outlined"
          label="Bairro"
          :rules="[required]"
          :readonly="readonly"
          :disabled="loader.loading"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.address"
          variant="outlined"
          label="Endereço"
          :rules="[required]"
          :readonly="readonly"
          :disabled="loader.loading"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.number"
          variant="outlined"
          label="Número"
          :rules="[required]"
          :readonly="readonly"
          :disabled="loader.loading"
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="model.complement"
          variant="outlined"
          label="Complemento"
          :readonly="readonly"
          :disabled="loader.loading"
        />
      </v-col>
    </v-row>
    <v-row v-if="!readonly" justify="end">
      <v-col cols="12" md="2">
        <v-btn
          color="primary"
          block
          :loading="loader.loading"
          :disabled="loader.loading"
          @click="submit"
        >
          {{ !!model.id ? 'Salvar' : 'Criar' }}
        </v-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script lang="ts" setup>
import { useDebouncer } from '@/helpers/debouncer'
import { required } from '@/helpers/rules/required'
import validateCEP from '@/helpers/rules/validateCEP'
import ViaCEPService from '@/services/ViaCEPService'
import { useLoader } from '@/stores/loader'
import { useSnackBar } from '@/stores/snackbar'
import Local from '@/types/Local'
import { ref, useTemplateRef } from 'vue'
import type { VForm } from 'vuetify/lib/components'

interface Props {
  readonly?: boolean
}

const loader = useLoader()
const snackbar = useSnackBar()
const { debounce } = useDebouncer()

const model = defineModel<Local>({ required: true })

const { readonly = false } = defineProps<Props>()

const emit = defineEmits<{
  submit: [ Local ]
}>()

// --- Form ---
const form = useTemplateRef<VForm>('form')

async function submit() {
  if (!form.value?.isValid) {
    snackbar.show(
      'Verifique se os campos e cargos foram preenchidos corretamente',
      'warning',
    )
    return
  }
  emit('submit', model.value)
}

// --- CEP ---
const invalidCEPs = ref<string[]>([])

const latestValidCEP = ref<string>()

async function fetchCepData() {
  const cep = model.value.zip_code.replace(/\D/g, '')
  if (cep.length !== 8 || invalidCEPs.value.includes(cep)) return

  try {
    loader.increment()

    const { data } = await (new ViaCEPService().search(cep))

    model.value.address = data.logradouro
    model.value.city = data.localidade
    model.value.state = data.uf.toUpperCase()
    model.value.neighborhood = data.bairro
    model.value.complement = data.complemento

    latestValidCEP.value = cep
  } catch (error) {
    snackbar.show('CEP Inválido', 'warning')

    invalidCEPs.value.push(cep)

    return error
  } finally {
    loader.decrement()
  }
}
</script>
